import { AppBuilderStreamAPIName, VisibilityRule } from 'src/constants';
import { buildApiEndpointUrl, getInitForApi } from 'src/utils/AmplifyApiUtils';

import type { MessagePart, SkillsConfig } from './builderSessionApi';

import { ApiMethods, ApiTags, appAPI } from '.';

export type AppBuilderStatus =
  | 'generating'
  | 'ready'
  | 'awaiting_config'
  | 'deploying'
  | 'deployed'
  | 'installed'
  | 'failed'
  // Terminal, set when the user hard-cancels an in-flight build. Like 'failed'
  // it is a non-success terminal state, so it belongs in neither
  // BUILD_TIMELINE_COMPLETE_STATUSES nor PUBLISH_ENABLED_STATUSES below.
  | 'stopped';

// Statuses where the build has produced a runnable app — everything past
// 'generating' except the terminal 'failed'. The build timeline treats any of
// these as "build complete" (defense-in-depth against out-of-order SSE) even if
// a per-task update lagged. Deliberately broader than PUBLISH_ENABLED_STATUSES:
// the timeline shows complete as soon as the app exists, whereas publishing only
// enables once the build is ready/installed/deployed.
export const BUILD_TIMELINE_COMPLETE_STATUSES: readonly AppBuilderStatus[] = [
  'ready',
  'awaiting_config',
  'deploying',
  'deployed',
  'installed',
];

// Statuses where the publish/deploy action is enabled — a strict subset of
// BUILD_TIMELINE_COMPLETE_STATUSES (see the note there for why they differ).
export const PUBLISH_ENABLED_STATUSES: readonly AppBuilderStatus[] = [
  'ready',
  'installed',
  'deployed',
];

export interface RequiredSecret {
  key: string;
  label: string;
  description: string;
  /**
   * The input affordance the manifest asked for — `url`, `api_key`, `secret` or
   * `text`.
   *
   * NOT a closed set at runtime, which is why this is a plain string: the
   * manifest is LLM-authored and the backend stores whatever it says ("No
   * semantic enforcement on the backend" — `RequiredSecret.Type`). Apps have
   * declared e.g. `password`. Every consumer needs a default branch, and for a
   * credential that default must be the safe one — see inputTypeForSecret.
   */
  type: string;
  docsUrl?: string;
  // Platform-provisioned secrets (e.g. CRON_SECRET) the backend generates and
  // injects at deploy. Never rendered in the config form and never gate publish.
  managed?: boolean;
}

/**
 * A single OAuth-connected provider the generated app declares in
 * `.assembly/app.json` under `requiredIntegrations`. Mirrors the Go
 * `app_builder.RequiredIntegration` struct.
 */
export interface RequiredIntegration {
  provider: string;
  scopes: string[];
}

export type IntegrationStatus =
  | 'connected'
  | 'not_connected'
  | 'needs_reauth'
  | 'needs_reauth_scope';

/**
 * Connection state of a single declared integration. `connectedAt` and
 * `connectedByInternalUserId` are absent when `status` is `not_connected`.
 * The `needs_reauth` statuses mean a connection row exists but broker calls
 * fail closed until the user reconnects. Mirrors the Go
 * `app_builder.IntegrationStatusResponse` struct.
 */
export interface IntegrationStatusResponse {
  provider: string;
  scopes: string[];
  status: IntegrationStatus;
  connectedAt?: number;
  connectedByInternalUserId?: string;
}

/**
 * Parsed `.assembly/app.json` surface manifest expressed as paths.
 * Combined with `sandboxUrl` or `deploymentUrl` to construct preview / live
 * URLs. An empty string means the surface was not declared in the manifest;
 * `surfaces` itself being undefined means no manifest has been parsed yet
 * (legacy builders, or builds that haven't completed once).
 */
export interface AppSurfaces {
  internalPath?: string;
  clientPath?: string;
  notificationPath?: string;
}

export interface QualityReport {
  score: number;
  review: string;
  evaluatedAt?: string;
}

/**
 * A parent-detectable feature the app's bundled @assembly-js/app-bridge
 * advertises. Mirrors the capability keys the SDK bakes into its manifest;
 * add a member here when the parent needs to gate behavior on a new bridge
 * feature. Kept as a string-literal union so call sites stay honest.
 */
export type BridgeCapability = 'app.ready';

/**
 * What the app's bundled SDKs can do, keyed by the component that provides
 * them. Derived from the app's committed manifests at build time on the
 * backend, so absence of a key means "not supported" — the correct reading
 * for older apps that predate a given capability.
 */
export interface AppCapabilities {
  bridge?: BridgeCapability[];
}

/**
 * Stamp recording that an app was saved as a Studio template, frozen at the
 * commit the template clones from.
 */
export interface PublishedTemplateStamp {
  commitSha: string;
  publishedAt: string;
  publishedBy: string;
}

/**
 * Response of POST /app-builders/:id/publish-template. Alongside the frozen
 * commit it carries a prefilled catalog card the curator pastes into Contentful
 * — there is no Contentful write path.
 */
export interface PublishTemplateOutput {
  templateId: string;
  name: string;
  icon: string;
  description: string;
  commitSha: string;
  publishedAt: string;
}

export interface AppBuilder {
  id: string;
  object: 'appBuilder';
  name: string;
  description: string;
  icon: string;
  status: AppBuilderStatus;
  prompt: string;
  useProductAgent?: boolean;
  deploymentUrl: string;
  sandboxUrl?: string;
  // Durable connection state of the sandbox dev server. Single source of truth
  // for the reconnecting overlay — do NOT infer connection from sandboxUrl
  // (that URL is stable across stop/resume and says nothing about liveness).
  // Backend always returns a concrete value (legacy rows are derived).
  sandboxState?: SandboxConnectState;
  deployErrors?: string;
  // Granular publish stage while status === 'deploying' (one of the DeployPhase
  // values: 'preparing' | 'publishing' | 'finalizing'). Streamed from the deploy
  // job so the publish popover can show real progress. Empty/absent otherwise.
  deployPhase?: string;
  /**
   * Set when a duplicate came up usable but incomplete — its copied schema never
   * reached its own database, so DB-backed requests fail until a publish re-runs the
   * app's build. Rendered as CloneSetupWarningBanner in the builder composer
   * (not via deployErrors — that means build/publish failure). Cleared by the
   * backend on successful publish (see CloneSetupWarning on the AppBuilder model).
   */
  cloneSetupWarning?: string;
  /**
   * True while this app is a duplicate whose source code has not landed in its repo
   * yet — the window in which it is legitimately `generating` without a build or a
   * plan. `status` alone cannot express it: a duplicate's later edit turn is
   * `generating` too. Derived server-side from the copy marker, which is itself
   * never exposed (it names the source's repo).
   */
  cloneCopyPending?: boolean;
  /**
   * 'app' | 'template' when this builder was cloned. Derived from write-once
   * provenance so the editor can pick the first-boot narration variant —
   * duplicate copy for same-workspace duplicates, install copy for template
   * installs (which share cloneCopyPending / cloneStage).
   */
  cloneSourceType?: string;
  /**
   * Contentful catalog pointer saved at template install
   * (`CloneProvenance.templateId`). Used to resolve the installed-template
   * chat card. Distinct from the forked source app id so lookup survives
   * versioning. Absent on app duplicates and non-clones.
   */
  templateId?: string;
  /**
   * Template installs only; true when the source's workspace-scoped context
   * selections were dropped at clone time.
   */
  droppedSelections?: boolean;
  /**
   * Granular stage of a duplicate's one-time source-code copy
   * ('copying' | 'schema' | 'preview'). Empty/absent on a copy-pending app means
   * the sandbox boot — the first step of the narration, "Preparing your
   * workspace" — which is already running before the page paints and so needs no
   * push of its own.
   *
   * The duplicate narration ends on the pushed empty string, and on nothing
   * else (template installs instead settle on builder status / lastDeployedAt):
   * `cloneCopyPending` flips false at the copy's ready flip, and a concurrent
   * reconnect can publish `sandboxState: 'connected'`, both of them BEFORE
   * 'preview' arrives. Ending on either leaves the last step unrendered.
   * Written only by the job that owns the copy, so `sandboxState` must not be
   * read as implying the earlier stages finished either.
   */
  cloneStage?: string;
  installedAppId: string;
  installedModuleId: string;
  modelConfig?: ModelConfig;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  requiredSecrets?: RequiredSecret[];
  /**
   * Declared, non-managed secret keys that currently hold a value. Keys only —
   * values never cross the API boundary, so this is the only way to tell a
   * configured secret from a blank one. Absent on legacy rows and on apps whose
   * manifest declares no secrets; treat undefined as "none configured".
   */
  configuredSecretKeys?: string[];
  /**
   * Declared, non-managed secret keys whose stored value differs from what the
   * live app is running — saved, but not yet published. Empty for an app that
   * has never deployed. Keys only, for the same reason as above.
   */
  pendingSecretKeys?: string[];
  requiredIntegrations?: RequiredIntegration[];
  surfaces?: AppSurfaces;
  // Internal performance metric anchors (POR-20126). Both nullable —
  // populated lazily on first plan turn / first ready transition, so
  // older AppBuilder rows will have them undefined.
  buildStartedAt?: string;
  firstReadyAt?: string;
  // Provider slugs that were connected when the build first reached ready — the
  // frozen snapshot the build-complete copy uses so its connection sentence stays
  // true to that moment, immune to a later connect/disconnect. Only ever present
  // and non-empty (a build that reused an existing connection). Absent means no
  // snapshot — a legacy row, or a build with nothing connected at ready — and the
  // copy falls back to live connection state. See backend
  // AppBuilder.ConnectedSlugsAtReady.
  connectedSlugsAtReady?: string[] | null;
  // ISO timestamp of the last successful deploy to clients. Absent on legacy
  // rows and apps that have never published; drives the "Last published …"
  // label in the builder header.
  lastDeployedAt?: string;
  // The structural layout the product agent chose. Absent when the product
  // agent was not used or did not finalize a layout. Drives the ?layout=
  // query param on the preview iframe so the sandbox's foundation page renders
  // a matching loading skeleton on first paint.
  layout?: {
    archetype: string;
    hasFilterBar: boolean;
    hasHeaderCta: boolean;
    hasTabs: boolean;
    regions: string[];
    contentWidth?: 'full' | 'narrow';
  };
  benchmarkRunId?: string;
  qualityReport?: QualityReport;
  hasUnpublishedChanges?: boolean | null;
  // Absent for every app never saved as a template, so a presence check is the
  // single signal for saved-vs-not.
  publishedAsTemplate?: PublishedTemplateStamp;
  // What the deployed app's bundled SDKs support, stamped at build time.
  // Undefined for legacy builders predating capability reporting.
  capabilities?: AppCapabilities;
  // Name/icon/visibility edits made in the publish popover that are deferred
  // until the deploy succeeds. Present only between a publish dispatch and its
  // completion (or after a failed publish, so the form can re-populate).
  pendingPublishSettings?: PendingPublishSettings;
}

/**
 * The publish-popover edits carried along with a deploy so the backend applies
 * them at the atomic success moment (in lockstep with the new version going
 * live) rather than before the deploy finishes. Each field is optional — only
 * the settings the user actually changed are sent.
 */
export interface PendingPublishSettings {
  name?: string;
  icon?: string;
  visibilityConfig?: VisibilityRule[];
}

/**
 * Presence check for a bridge capability. Returns false when the builder is
 * undefined or the capability isn't listed — i.e. old apps and apps built
 * before the capability existed are treated as not supporting it.
 */
export const hasBridgeCapability = (
  builder: AppBuilder | undefined,
  capability: BridgeCapability,
): boolean => builder?.capabilities?.bridge?.includes(capability) ?? false;

export interface SkillInfo {
  name: string;
  description: string;
}

export interface PromptInfo {
  filename: string;
  content: string;
}

export interface PromptSlotVersion {
  id: string;
  title: string;
  version: number;
  isDefault: boolean;
  updatedAt: string;
}

export interface PromptSlot {
  slug: string;
  label: string;
  category: string;
  defaultContent: string;
  versions: PromptSlotVersion[] | null;
}

export interface BuildConfigResponse {
  skills: SkillInfo[];
  prompts: {
    planner: PromptInfo;
    builder: PromptInfo;
  };
  slots?: PromptSlot[];
}

export interface UpdateAppBuilderInput {
  id: string;
  name?: string;
  icon?: string;
}

/**
 * Reference to an uploaded attachment file. The webapp uploads files
 * directly to the user-files bucket via Amplify Storage.put at
 * level=protected, then echoes the resulting relative fileKey plus the
 * uploader's Cognito identityId back to the backend on the sendMessage
 * call. The backend reconstructs the full S3 path on its end when
 * presigning GETs for the sandbox turn program.
 */
export interface AppBuilderAttachment {
  id: string;
  name: string;
  fileKey: string;
  identityId: string;
  contentType?: string;
  size?: number;
}

export const isValidAttachment = (
  value: unknown,
): value is AppBuilderAttachment => {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return (['id', 'name', 'fileKey', 'identityId'] as const).every(
    (field) => typeof record[field] === 'string' && record[field] !== '',
  );
};

export type AppBuilderMessageActionHint =
  | 'resume'
  | 'send_followup'
  | 'skip'
  | 'allow';

export interface AppBuilderMessage {
  id: string;
  content: string;
  type: string;
  role: string;
  turnId?: string;
  createdAt?: string;
  updatedAt?: string;
  actionHints?: AppBuilderMessageActionHint[];
  /** Raw message parts from the builder session API. Populated for task_list messages. */
  parts?: MessagePart[];
  narratorStatus?: 'forming' | 'complete';
  /** 0-based index of the thinking paragraph this narration summarizes; the
   * Decision Stream orders lines by it (stable across out-of-order completions). */
  narratorParagraphIndex?: number;
  attachments?: AppBuilderAttachment[];
  /** Client-only: the builder session an optimistic bubble was created for, so
   * the bubble can be filtered out of any other session's thread. Real persisted
   * messages are already session-guarded upstream in useBuilderSessionSSE. */
  builderSessionId?: string;
  /** Client-only: the id this optimistic send was minted with and sent to the
   * backend as `clientMessageId`. The backend adopts it as the persisted
   * message's id, so the bubble (and its durable mirror) reconcile by exact id
   * rather than content. Absent on server messages. */
  clientMessageId?: string;
  /** First message of a fresh context after a "New session" reset. The
   * transcript draws the new-session divider directly above this message —
   * anchored by identity, never by timestamp ordering. */
  startsNewSession?: boolean;
}

export interface AppBuilderVersionDiff {
  versionId: string;
  versionNumber: number;
  createdAt: string;
  changedFiles: string[];
  addedFiles?: string[];
  removedFiles?: string[];
}

// Product agent types

export interface ProductAgentWizardContext {
  name: string;
  description: string;
  prompt: string;
  icon: string;
}

export interface ProductAgentMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ProductAgentQuestionOption {
  label: string;
  description: string;
}

export interface ProductAgentQuestion {
  question: string;
  header: string;
  options: ProductAgentQuestionOption[];
  multiSelect: boolean;
  // Both default to true when undefined; the card applies the defaulting.
  allowFreeText?: boolean;
  skippable?: boolean;
}

export interface ProductAgentChatInput {
  wizardContext: ProductAgentWizardContext;
  messages: ProductAgentMessage[];
}

export interface ProductAgentResponse {
  message: string;
  questions?: ProductAgentQuestion[];
  isComplete: boolean;
  finalizedPrompt?: string;
}

export class AppBuilderStreamError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'AppBuilderStreamError';
    this.status = status;
  }
}

const getStreamErrorMessage = async (response: Response) => {
  try {
    const payload = await response.json();
    if (
      payload &&
      typeof payload === 'object' &&
      'message' in payload &&
      typeof payload.message === 'string'
    ) {
      return payload.message as string;
    }
  } catch (error) {
    // Fall through to the generic message when the response is not JSON.
  }

  return 'Unable to stream message to app builder';
};

export interface PlanTask {
  id: string;
  /** Human-readable headline shown to non-technical reviewers. */
  title: string;
  /** 1–2 sentence non-technical summary of what this task delivers. */
  userSummary: string;
  /** Technical implementation instructions for the build agent (LLM). */
  description: string;
  stepsToVerify: string[];
}

export const isPlanTask = (value: unknown): value is PlanTask => {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === 'string' &&
    typeof v.title === 'string' &&
    typeof v.userSummary === 'string' &&
    typeof v.description === 'string' &&
    Array.isArray(v.stepsToVerify) &&
    v.stepsToVerify.every((s) => typeof s === 'string')
  );
};

export const isPlanTaskArray = (value: unknown): value is PlanTask[] =>
  Array.isArray(value) && value.every(isPlanTask);

export interface BuildEvent {
  type:
    | 'task_started'
    | 'task_log'
    | 'task_completed'
    | 'task_failed'
    | 'deploying'
    | 'completed'
    | 'error';
  taskId?: string;
  line?: string;
  url?: string;
  message?: string;
  token?: string;
}

export interface ModelConfig {
  planningModel?: string;
  planEffort?: string;
  buildModel?: string;
  buildEffort?: string;
  noTaskList?: boolean;
  skipParallelism?: boolean;
}

export interface PrepareBuildOutput {
  appBuilderId: string;
  sandboxName: string;
  repoFullName: string;
  marketplaceAppId: string;
  // BuilderSession created alongside the AppBuilder; the kickoff flow
  // navigates to this session instead of issuing a separate create call.
  sessionId: string;
}

export interface StartBuildOutput {
  sessionId: string;
  activeTaskListMessageId: string;
}

export async function prepareBuild(
  appName?: string,
  modelConfig?: ModelConfig,
  prompt?: string,
  useProductAgent?: boolean,
  skillsConfig?: SkillsConfig,
  promptSelections?: Record<string, string>,
): Promise<PrepareBuildOutput> {
  const requestInit = await getInitForApi(AppBuilderStreamAPIName, {
    method: 'POST',
    body: JSON.stringify({
      appName,
      modelConfig,
      prompt,
      useProductAgent,
      ...(skillsConfig ? { skillsConfig } : {}),
      ...(promptSelections ? { promptSelections } : {}),
    }),
    headers: { 'Content-Type': 'application/json' },
  });

  const response = await fetch(
    buildApiEndpointUrl(AppBuilderStreamAPIName, '/app-builders/prepare-build'),
    requestInit,
  );

  if (!response.ok) {
    const errorMessage = await getStreamErrorMessage(response);
    throw new AppBuilderStreamError(errorMessage, response.status);
  }

  return response.json() as Promise<PrepareBuildOutput>;
}

export async function startBuild(
  appBuilderId: string,
  sessionId: string,
): Promise<StartBuildOutput> {
  const requestInit = await getInitForApi(AppBuilderStreamAPIName, {
    method: 'POST',
    body: JSON.stringify({
      sessionId,
    }),
    headers: { 'Content-Type': 'application/json' },
  });

  const response = await fetch(
    buildApiEndpointUrl(
      AppBuilderStreamAPIName,
      `/app-builders/${appBuilderId}/build`,
    ),
    requestInit,
  );

  if (!response.ok) {
    const errorMessage = await getStreamErrorMessage(response);
    throw new AppBuilderStreamError(errorMessage, response.status);
  }

  return response.json() as Promise<StartBuildOutput>;
}

export async function retryBuild(
  appBuilderId: string,
  sessionId: string,
): Promise<void> {
  const requestInit = await getInitForApi(AppBuilderStreamAPIName, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  const response = await fetch(
    buildApiEndpointUrl(
      AppBuilderStreamAPIName,
      `/app-builders/${appBuilderId}/sessions/${sessionId}/retry`,
    ),
    requestInit,
  );

  if (!response.ok) {
    const errorMessage = await getStreamErrorMessage(response);
    throw new AppBuilderStreamError(errorMessage, response.status);
  }
}

/** Re-wake and re-dispatch for an existing user message or plan approval
 *  (no duplicate user row). */
export type RetryTurnSetupParams = {
  messageId?: string;
  approvalMode?: boolean;
  approvedMsgId?: string;
  failedTurnId?: string;
};

export async function retryTurnSetup(
  appBuilderId: string,
  sessionId: string,
  params: RetryTurnSetupParams,
): Promise<void> {
  const requestInit = await getInitForApi(AppBuilderStreamAPIName, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...(params.messageId ? { messageId: params.messageId } : {}),
      ...(params.approvalMode ? { approvalMode: true } : {}),
      ...(params.approvedMsgId ? { approvedMsgId: params.approvedMsgId } : {}),
      ...(params.failedTurnId ? { failedTurnId: params.failedTurnId } : {}),
    }),
  });

  const response = await fetch(
    buildApiEndpointUrl(
      AppBuilderStreamAPIName,
      `/app-builders/${appBuilderId}/sessions/${sessionId}/retry-setup`,
    ),
    requestInit,
  );

  if (!response.ok) {
    const errorMessage = await getStreamErrorMessage(response);
    throw new AppBuilderStreamError(errorMessage, response.status);
  }
}

export const getAppBuilderEntityTag = (id: string) => ({
  type: ApiTags.appBuilders as const,
  id,
});

export const getAppBuilderListTag = () => ({
  type: ApiTags.appBuilders as const,
  id: 'LIST',
});

export const getAppBuilderIntegrationsTag = (id: string) => ({
  type: ApiTags.appBuilderIntegrations as const,
  id,
});

export type SandboxConnectState =
  | 'connected'
  | 'connecting'
  | 'refreshing'
  | 'disconnected'
  | 'error';

export interface ReconnectSandboxResponse {
  // The synchronous reconnect endpoint only ever returns connected or
  // connecting; the async job later drives the builder's sandboxState to
  // connected or disconnected.
  state: 'connected' | 'connecting';
  sandboxUrl?: string;
}

/** Which viewer the QA testing agent should be minted a session token as. */
export type QAEmbedIdentity = 'internal' | 'client';

/** Which host renders the embed — see QAEmbedInput.Target in interfaces.go. */
export type QAEmbedTarget = 'deployed' | 'sandbox';

export interface QAEmbedInput {
  id: string;
  identity: QAEmbedIdentity;
  internalUserId?: string;
  clientUserId?: string;
  companyId?: string;
  // NotificationID, when set, mints the notification surface instead of the
  // surface `identity` picks. Requires identity="internal" (see
  // ErrNotificationRequiresInternalIdentity in errors.go).
  notificationId?: string;
  instanceId?: string;
  target?: QAEmbedTarget;
}

export interface QAEmbedOutput {
  embedUrl: string;
  identity: QAEmbedIdentity;
  appId: string;
  portalId: string;
}

export const appBuilderApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    listAppBuilders: build.query<AppBuilder[], void>({
      query: () => ({
        path: '/app-builders',
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: (result) => {
        const itemTags =
          result?.map((item) => getAppBuilderEntityTag(item.id)) ?? [];
        return [getAppBuilderListTag(), ...itemTags];
      },
    }),
    getAppBuilder: build.query<AppBuilder, { id: string }>({
      query: ({ id }) => ({
        path: `/app-builders/${id}`,
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: (_, __, arg) => [getAppBuilderEntityTag(arg.id)],
    }),
    deployAppBuilder: build.mutation<
      AppBuilder,
      { id: string; pendingSettings?: PendingPublishSettings }
    >({
      query: ({ id, pendingSettings }) => ({
        path: `/app-builders/${id}/deploy`,
        method: ApiMethods.post,
        // Only send a body when there are deferred edits, so a plain publish
        // stays a bodyless request.
        options: pendingSettings ? { body: pendingSettings } : {},
      }),
      // Optimistically stamp the deferred edits onto the cached builder so the
      // publish popover shows the new name/icon/visibility immediately while the
      // deploy runs — the backend persists the same pendingPublishSettings, so
      // the follow-up refetch (via the invalidated tag) confirms it. Reverted if
      // the deploy request itself fails.
      async onQueryStarted(
        { id, pendingSettings },
        { dispatch, queryFulfilled },
      ) {
        if (!pendingSettings) return;
        // An empty body is a "clear" signal (the user reverted their edits), so
        // reflect that by dropping the cached pending settings.
        const isClear =
          pendingSettings.name === undefined &&
          pendingSettings.icon === undefined &&
          pendingSettings.visibilityConfig === undefined;
        const patch = dispatch(
          appBuilderApi.util.updateQueryData(
            'getAppBuilder',
            { id },
            (draft) => {
              draft.pendingPublishSettings = isClear
                ? undefined
                : pendingSettings;
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_, __, arg) => [
        getAppBuilderEntityTag(arg.id),
        getAppBuilderListTag(),
      ],
    }),
    submitAppBuilderSecrets: build.mutation<
      AppBuilder,
      { id: string; values: Record<string, string> }
    >({
      query: ({ id, values }) => ({
        path: `/app-builders/${id}/secrets`,
        method: ApiMethods.post,
        options: { body: { values } },
      }),
      invalidatesTags: (_, __, arg) => [
        getAppBuilderEntityTag(arg.id),
        getAppBuilderListTag(),
      ],
    }),
    // PARTIAL update: only the keys present in `values` are written, and every
    // omitted key keeps its stored value. Distinct from submitAppBuilderSecrets
    // above, which is the awaiting_config gate's all-at-once POST and deploys on
    // success. The response never carries values — the useful parts are the
    // refreshed configuredSecretKeys and hasUnpublishedChanges. Refused with a
    // 409 while the app is publishing.
    updateAppBuilderSecrets: build.mutation<
      AppBuilder,
      { id: string; values: Record<string, string> }
    >({
      query: ({ id, values }) => ({
        path: `/app-builders/${id}/secrets`,
        method: ApiMethods.patch,
        options: { body: { values } },
      }),
      // The LIST tag is what makes a save on one surface show up on the other:
      // the settings page reads the builders list, the build page reads the
      // entity. No optimistic update — claiming a credential was stored before
      // the backend confirmed it is the wrong way to be wrong.
      invalidatesTags: (_, __, arg) => [
        getAppBuilderEntityTag(arg.id),
        getAppBuilderListTag(),
      ],
    }),
    createIntegrationConnectSession: build.mutation<
      { sessionToken: string },
      { id: string; provider: string }
    >({
      query: ({ id, provider }) => ({
        path: `/app-builders/${id}/integrations/${provider}/connect-session`,
        method: ApiMethods.post,
        options: {},
      }),
    }),
    submitIntegrationApiKey: build.mutation<
      IntegrationStatusResponse[],
      { id: string; provider: string; apiKey: string }
    >({
      query: ({ id, provider, apiKey }) => ({
        path: `/app-builders/${id}/integrations/${provider}/api-key`,
        method: ApiMethods.post,
        options: { body: { apiKey } },
      }),
      transformResponse: (response: {
        integrations: IntegrationStatusResponse[];
      }) => response.integrations,
      // The resulting connection is workspace-scoped, so the Settings
      // Connections page caches must refresh too. The builder's own
      // integrations list is refetched by the caller (mirroring the OAuth
      // connect flow), not via tags.
      invalidatesTags: [
        ApiTags.portalConnections,
        ApiTags.portalConnectionsAvailable,
      ],
    }),
    // The `basic` analogue of submitIntegrationApiKey: same synchronous import
    // contract, but the credential is a username/password pair (Twilio
    // repurposes it as Account SID + Auth Token).
    submitIntegrationBasicAuth: build.mutation<
      IntegrationStatusResponse[],
      { id: string; provider: string; username: string; password: string }
    >({
      query: ({ id, provider, username, password }) => ({
        path: `/app-builders/${id}/integrations/${provider}/basic`,
        method: ApiMethods.post,
        options: { body: { username, password } },
      }),
      transformResponse: (response: {
        integrations: IntegrationStatusResponse[];
      }) => response.integrations,
      invalidatesTags: [
        ApiTags.portalConnections,
        ApiTags.portalConnectionsAvailable,
      ],
    }),
    getAppBuilderIntegrations: build.query<
      IntegrationStatusResponse[],
      { id: string }
    >({
      query: ({ id }) => ({
        path: `/app-builders/${id}/integrations`,
        method: ApiMethods.get,
        options: {},
      }),
      transformResponse: (response: {
        integrations: IntegrationStatusResponse[];
      }) => response.integrations,
      providesTags: (_, __, arg) => [getAppBuilderIntegrationsTag(arg.id)],
    }),
    updateAppBuilder: build.mutation<AppBuilder, UpdateAppBuilderInput>({
      query: ({ id, ...body }) => ({
        path: `/app-builders/${id}`,
        method: ApiMethods.patch,
        options: { body },
      }),
      // A rename updates the linked install's label in module_settings, so the
      // installs (apps) cache that drives sidebar rendering must be refreshed.
      invalidatesTags: (_, __, arg) => [
        getAppBuilderEntityTag(arg.id),
        ApiTags.apps,
      ],
    }),
    deleteAppBuilder: build.mutation<{ success: boolean }, { id: string }>({
      query: ({ id }) => ({
        path: `/app-builders/${id}`,
        method: ApiMethods.del,
        options: {},
      }),
      // The backend's app-builder delete also removes the linked install from
      // module_settings, so invalidate the installs (apps) cache too.
      invalidatesTags: [ApiTags.appBuilders, ApiTags.apps],
    }),
    transferAppBuilder: build.mutation<
      AppBuilder,
      // destinationUserId is required: every row the move re-keys also carries
      // an actor, and the requester exists only in the source workspace.
      { id: string; destinationPortalId: string; destinationUserId: string }
    >({
      query: ({ id, destinationPortalId, destinationUserId }) => ({
        path: `/app-builders/${id}/transfer`,
        method: ApiMethods.post,
        options: { body: { destinationPortalId, destinationUserId } },
      }),
      // A transfer moves the app out of this workspace entirely, so drop it
      // from the app-builder list and refresh the installs (apps) cache that
      // drives sidebar rendering.
      invalidatesTags: [ApiTags.appBuilders, ApiTags.apps],
    }),
    // The UI verb is 'Save as template'; the route is still publish-template.
    saveAppBuilderAsTemplate: build.mutation<
      PublishTemplateOutput,
      { id: string }
    >({
      query: ({ id }) => ({
        path: `/app-builders/${id}/publish-template`,
        method: ApiMethods.post,
        options: {},
      }),
      // Only the builder gains a stamp — saving leaves the installs and apps
      // caches untouched, unlike a transfer which moves the app out entirely.
      invalidatesTags: (_, __, arg) => [getAppBuilderEntityTag(arg.id)],
    }),
    cloneAppBuilder: build.mutation<
      PrepareBuildOutput,
      // Exactly one of sourceAppBuilderId / templateId — the backend
      // rejects both or neither. Duplicate uses the former; template install
      // (Browse modal + marketing handoff) uses the latter.
      | { sourceAppBuilderId: string; name?: string }
      | { templateId: string; name?: string }
    >({
      query: (body) => ({
        path: '/app-builders/clone',
        method: ApiMethods.post,
        options: { body },
      }),
      // A clone creates both a new builder row and a new install row, so refresh
      // the app-builder list alongside the installs (apps) cache that drives the
      // sidebar and the app-setup list. Template installs also advance Studio
      // firstApp server-side — refetch onboarding so the checklist updates.
      invalidatesTags: [
        ApiTags.appBuilders,
        ApiTags.apps,
        ApiTags.onboarding_task_status,
      ],
    }),
    getBuildConfig: build.query<BuildConfigResponse, void>({
      query: () => ({
        path: '/app-builders/build-config',
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: [{ type: ApiTags.contextItems, id: 'LIST' }],
    }),
    reconnectSandbox: build.mutation<ReconnectSandboxResponse, { id: string }>({
      query: ({ id }) => ({
        path: `/app-builders/${id}/sandbox/reconnect`,
        method: ApiMethods.post,
        options: {},
      }),
      // Refetch the builder so the persisted sandboxState (connecting) surfaces
      // immediately rather than on the next 3s poll — the overlay reads it.
      invalidatesTags: (_, __, arg) => [getAppBuilderEntityTag(arg.id)],
    }),
    stopBuilderTurn: build.mutation<
      StopTurnResponse,
      { id: string; sessionId: string }
    >({
      query: ({ id, sessionId }) => ({
        path: `/app-builders/${id}/sessions/${sessionId}/stop`,
        method: ApiMethods.post,
        options: {},
      }),
      // Refetch the builder so the status flip (generating → stopped) surfaces
      // immediately rather than on the next poll.
      invalidatesTags: (_, __, arg) => [getAppBuilderEntityTag(arg.id)],
    }),
    getQAEmbed: build.query<QAEmbedOutput, QAEmbedInput>({
      query: ({ id, ...params }) => ({
        path: `/app-builders/${id}/qa-embed`,
        method: ApiMethods.get,
        options: {
          // Mirrors QAEmbedInput in backend/lib/resources/app_builder/interfaces.go
          // — every field is forwarded as-is, and GetQAEmbed's permutation
          // table decides which combination is a valid identity. Undefined
          // fields are dropped rather than sent as the literal string
          // "undefined", which the backend would treat as a non-empty value.
          queryStringParameters: Object.fromEntries(
            Object.entries(params).filter(([, value]) => value !== undefined),
          ),
        },
      }),
    }),
  }),
});

export interface StopTurnResponse {
  sessionId: string;
  turnId?: string;
  stopped: boolean;
}

export const {
  useListAppBuildersQuery,
  useGetAppBuilderQuery,
  useDeployAppBuilderMutation,
  useSubmitAppBuilderSecretsMutation,
  useUpdateAppBuilderSecretsMutation,
  useCreateIntegrationConnectSessionMutation,
  useSubmitIntegrationApiKeyMutation,
  useSubmitIntegrationBasicAuthMutation,
  useGetAppBuilderIntegrationsQuery,
  useUpdateAppBuilderMutation,
  useDeleteAppBuilderMutation,
  useTransferAppBuilderMutation,
  useSaveAppBuilderAsTemplateMutation,
  useCloneAppBuilderMutation,
  useGetBuildConfigQuery,
  useReconnectSandboxMutation,
  useStopBuilderTurnMutation,
  useGetQAEmbedQuery,
} = appBuilderApi;
